export interface ErrorLogMeta {
  message: string;
  name?: string;
  code?: string;
  cause?: string;
  causes?: string[];
  stack?: string;
}

function formatUnknownValue(value: unknown): string {
  if (value == null) return String(value);
  if (value instanceof Error) return value.message || value.name || 'Error';
  if (typeof value === 'object') {
    const obj = value as { message?: unknown; code?: unknown };
    if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
    if (typeof obj.code === 'string' && obj.code.trim()) return obj.code;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function getNextCause(value: unknown): unknown | undefined {
  if (value == null || typeof value !== 'object' || !('cause' in value)) return undefined;
  const cause = (value as { cause?: unknown }).cause;
  return cause == null ? undefined : cause;
}

function collectCauseChain(err: unknown): string[] {
  const causes: string[] = [];
  const seen = new Set<unknown>();
  let current = getNextCause(err);

  while (current != null && !seen.has(current)) {
    seen.add(current);
    causes.push(formatUnknownValue(current));
    const next = getNextCause(current);
    if (next == null || seen.has(next)) break;
    current = next;
  }

  return causes;
}

/** Structured fields for logging, always including err.cause when present. */
export function errorLogMeta(err: unknown): ErrorLogMeta {
  if (err instanceof Error) {
    const causes = collectCauseChain(err);
    const code =
      'code' in err && typeof (err as NodeJS.ErrnoException).code === 'string'
        ? (err as NodeJS.ErrnoException).code
        : undefined;
    return {
      message: err.message || err.name || 'Error',
      name: err.name,
      ...(code ? { code } : {}),
      ...(causes[0] ? { cause: causes[0] } : {}),
      ...(causes.length > 1 ? { causes } : {}),
      ...(err.stack ? { stack: err.stack } : {}),
    };
  }

  const causes = collectCauseChain(err);
  const record = err && typeof err === 'object' ? (err as Record<string, unknown>) : null;
  const code = typeof record?.code === 'string' ? record.code : undefined;

  return {
    message: formatUnknownValue(err),
    ...(code ? { code } : {}),
    ...(causes[0] ? { cause: causes[0] } : {}),
    ...(causes.length > 1 ? { causes } : {}),
  };
}

export function formatErrorMessage(err: unknown, fallback = 'Unknown error'): string {
  const meta = errorLogMeta(err);
  const message = !meta.message || meta.message === 'undefined' ? fallback : meta.message;

  const causeText =
    meta.causes && meta.causes.length > 1
      ? meta.causes.join(' → ')
      : meta.cause;

  if (!causeText) return message;

  const lowerMessage = message.toLowerCase();
  const lowerCause = causeText.toLowerCase();
  if (lowerMessage.includes(lowerCause)) return message;

  return `${message} (cause: ${causeText})`;
}

/** Prefix + error message with cause, for toast/dialog copy. */
export function formatPrefixedToastError(prefix: string, err: unknown, fallback = 'Unknown error'): string {
  const msg = formatErrorMessage(err, fallback);
  if (prefix === msg || prefix.endsWith(`: ${msg}`)) return prefix;
  return `${prefix}: ${msg}`;
}

/** Renderer-safe console.error with cause chain. */
export function logConsoleError(label: string, err: unknown, extra?: Record<string, unknown>): void {
  console.error(label, { ...errorLogMeta(err), ...extra });
}
